import React, { useEffect, useState } from "react";
import { Video, Mic, MicOff, VideoOff, Phone, Sparkles } from "lucide-react";
import { Page, Card, PrimaryButton } from "../components/UI";
import api from "../lib/api";
import { toast } from "sonner";

export default function Telehealth() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [session, setSession] = useState(null);
  const [note, setNote] = useState("");
  const [transcript, setTranscript] = useState("");
  const [suggestions, setSuggestions] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data));
  }, []);

  const start = async () => {
    if (!selectedClient) return toast.error("Select a client");
    try {
      const { data } = await api.post("/telehealth/session", { client_id: selectedClient });
      setSession(data);
      toast.success("Telehealth session started");
    } catch { toast.error("Failed to start"); }
  };

  const getSuggestions = async () => {
    if (!session || !transcript.trim()) return toast.error("Add a transcript snippet first");
    setSuggesting(true);
    try {
      const { data } = await api.post(`/telehealth/${session.id}/suggest`, { transcript });
      setSuggestions(data.suggestions);
    } catch { toast.error("Failed"); }
    setSuggesting(false);
  };

  const acceptSuggestions = () => {
    setNote(note + "\n\n" + suggestions);
    setSuggestions("");
    toast.success("Added to note");
  };

  const end = () => {
    setSession(null);
    setTranscript("");
    setSuggestions("");
    setNote("");
    toast.success("Session ended");
  };

  if (!session) {
    return (
      <Page title="Split-Screen Telehealth" subtitle="Video on the left. Live note editor on the right. AI suggests content as you go.">
        <Card className="max-w-xl mx-auto text-center py-12">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: "var(--mf-primary)" }}>
            <Video className="w-8 h-8 text-white" strokeWidth={1.5} />
          </div>
          <h2 className="font-serif text-2xl mb-2">Start a session</h2>
          <p className="text-sm mb-6 max-w-sm mx-auto" style={{ color: "var(--mf-text-muted)" }}>HIPAA-compliant video with AI-assisted live note taking.</p>
          <select value={selectedClient} onChange={(e) => setSelectedClient(e.target.value)} className="w-full max-w-xs mx-auto px-4 py-3 rounded-xl outline-none text-sm mb-4 block" style={{ background: "var(--mf-muted)" }} data-testid="telehealth-client-select">
            <option value="">Select client…</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
          </select>
          <PrimaryButton onClick={start} testid="telehealth-start-btn">Start Session</PrimaryButton>
        </Card>
      </Page>
    );
  }

  return (
    <Page title="Live Session" subtitle={`Room ${session.room_id} · Active`}>
      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="!p-0 overflow-hidden relative min-h-[480px]" testid="video-panel">
          <div className="relative w-full h-full min-h-[480px] flex items-center justify-center" style={{ background: "linear-gradient(135deg, #1A6B72, #0F1B2D)" }}>
            {camOn ? (
              <div className="text-center">
                <div className="w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center text-white font-serif text-3xl" style={{ background: "rgba(255,255,255,0.15)" }}>
                  {clients.find(c => c.id === selectedClient)?.first_name?.[0]}
                </div>
                <div className="text-white font-serif text-xl">{clients.find(c => c.id === selectedClient)?.first_name} {clients.find(c => c.id === selectedClient)?.last_name}</div>
                <div className="text-white/60 text-sm mt-1">Connected · HIPAA-secure</div>
              </div>
            ) : (
              <div className="text-white/60"><VideoOff className="w-12 h-12 mx-auto mb-2" /><div>Camera off</div></div>
            )}
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-3">
              <button onClick={() => setMicOn(!micOn)} className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: micOn ? "rgba(255,255,255,0.15)" : "#B45253" }} data-testid="mic-toggle">
                {micOn ? <Mic className="w-5 h-5 text-white" /> : <MicOff className="w-5 h-5 text-white" />}
              </button>
              <button onClick={() => setCamOn(!camOn)} className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: camOn ? "rgba(255,255,255,0.15)" : "#B45253" }} data-testid="cam-toggle">
                {camOn ? <Video className="w-5 h-5 text-white" /> : <VideoOff className="w-5 h-5 text-white" />}
              </button>
              <button onClick={end} className="w-14 h-12 rounded-full flex items-center justify-center" style={{ background: "#B45253" }} data-testid="end-call-btn">
                <Phone className="w-5 h-5 text-white rotate-[135deg]" />
              </button>
            </div>
          </div>
        </Card>

        <div className="space-y-4">
          <Card testid="live-note-editor">
            <h3 className="font-serif text-xl mb-3">Live Note</h3>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={10} placeholder="Type notes while the session runs…" className="w-full px-4 py-3 rounded-xl outline-none text-sm resize-none" style={{ background: "var(--mf-muted)" }} data-testid="live-note-textarea"></textarea>
          </Card>

          <Card testid="ai-suggestions-panel">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-serif text-xl flex items-center gap-2"><Sparkles className="w-4 h-4" style={{ color: "var(--mf-accent)" }} /> AI Suggestions</h3>
            </div>
            <textarea value={transcript} onChange={(e) => setTranscript(e.target.value)} rows={3} placeholder="Paste a transcript snippet (with consent)…" className="w-full px-4 py-2.5 rounded-xl outline-none text-sm resize-none mb-3" style={{ background: "var(--mf-muted)" }} data-testid="transcript-input"></textarea>
            <div className="flex gap-2">
              <PrimaryButton onClick={getSuggestions} disabled={suggesting} testid="get-suggestions-btn">{suggesting ? "Thinking…" : "Get Suggestions"}</PrimaryButton>
              {suggestions && <button onClick={acceptSuggestions} className="px-4 py-2 rounded-full text-sm" style={{ background: "var(--mf-secondary)", color: "#FAFAF8" }} data-testid="accept-suggestions-btn">Accept & Add</button>}
            </div>
            {suggestions && <div className="mt-3 p-3 rounded-xl text-sm whitespace-pre-wrap mf-prose" style={{ background: "rgba(201,168,76,0.1)" }}>{suggestions}</div>}
          </Card>
        </div>
      </div>
    </Page>
  );
}
